import { CursorPageQuery, CursorPaged } from '../../types/definitions'
import { makeObservable, action, computed } from 'mobx'
import WorkspacePostingOpportunity, {
  WorkspacePostingStatus,
} from './WorkspacePostingOpportunity'
import WorkspacePostingOpportunityViewModel from './WorkspacePostingOpportunityViewModel'
import WorkspacePostingOpportunitiesSearchViewModel from './WorkspacePostingOpportunitiesSearchViewModel'
import WorkspaceOpportunityDetailsViewModel from './WorkspaceOpportunityDetailsViewModel'
import { AlertFactory } from '../../alerts/Alert'
import WorkspaceOpportunitiesAdapter from './WorkspaceOpportunitiesAdapter'
import { ViewModelList } from '../../infra/ViewModelList'
import WorkspaceOpportunityInvitationsViewModel from '../invitations/WorkspaceOpportunityInvitationsViewModel'
import WorkspaceOpportunityInvitationStore from '../invitations/WorkspaceOpportunityInvitationStore'

export default class WorkspacePostingOpportunitiesViewModel {
  private _fetchOpportunities = (
    query: CursorPageQuery,
  ): Promise<CursorPaged<WorkspacePostingOpportunity>> =>
    this.workspacePostingOpportunityPort.query(query)

  /**
   * Workspace Opportunities view model list.
   */
  private readonly opportunityVMs: ViewModelList<
    WorkspacePostingOpportunity,
    WorkspacePostingOpportunityViewModel
  >

  /**
   * Opportunity searcher.
   */
  searcher = new WorkspacePostingOpportunitiesSearchViewModel(
    this._fetchOpportunities,
  )

  detailsVM = new WorkspaceOpportunityDetailsViewModel(
    this.workspacePostingOpportunityPort,
    this.newAlert,
  )

  invitationsVM = this.workspaceOpportunityInvitationStore
    ? new WorkspaceOpportunityInvitationsViewModel(
        this.workspaceOpportunityInvitationStore,
        this.newAlert,
      )
    : null

  constructor(
    private workspacePostingOpportunityPort: WorkspaceOpportunitiesAdapter,
    private newAlert: AlertFactory,
    private workspaceOpportunityInvitationStore: WorkspaceOpportunityInvitationStore | null,
  ) {
    makeObservable(this, {
      activate: action.bound,
      deactivate: action.bound,
      opportunities: computed,
    })
    this.opportunityVMs = new ViewModelList({
      models: () => this.searcher.scroller.items.slice(),
      create: (model) =>
        new WorkspacePostingOpportunityViewModel(
          () => this.detailsVM.disabled,
          model,
        ),
    })
  }

  get opportunities() {
    return this.opportunityVMs.items.filter(
      (vm) =>
        vm.opportunity.status === WorkspacePostingStatus.Active ||
        (vm.opportunity.status === WorkspacePostingStatus.InReview &&
          vm.opportunity.isApplicant),
    )
  }

  activate() {
    this.searcher.activate()
    this.opportunityVMs.activate()
  }

  /**
   * Deactivate the view model.
   */
  deactivate() {
    // Deselect any selected op when leaving the Ops view.
    this.detailsVM.deselect()
    this.opportunityVMs.deactivate()
  }
}
